import { add_operation, apply, type RulesLogic } from "json-logic-js";
import _ from "lodash";
import Mustache from "mustache";
import XRegExp from "xregexp";

import { type LabelRuleNotificationSettings } from "./labels";

export interface LabelRuleGroup {
  labelKey: string;
  active: boolean;
  labelRules: LabelRule[];
  notificationSettings: LabelRuleNotificationSettings;
  level: string;
  createdAt: string;
  updatedAt: string;
}

export interface LabelRule {
  name: string;
  createdAt: string;
  active: boolean;
  priority: number;
  matcher: RulesLogic;
  _regexCaptures?: RegexCapture[];
  output: string;
}

export interface LabelTargetFields {
  name: string;
  iconType: string;
  nbt: {
    kind: string;
    platform: string;
    campaignName: string;
    adsetName: string;
    adName: string;
  };
  utm: {
    source: string;
    medium: string;
    campaign: string;
    term: string;
    content: string;
  };
  adKey: {
    kind: string;
    platform: string;
    campaign: string;
    adset: string;
    ad: string;
  };
  referral: {
    domain: string;
  };
  google__campaign: {
    advertisingChannelType: string;
    advertisingChannelSubType: string;
    isDynamicSearch: string;
  };
  facebook__campaign: {
    objective: string;
  };
  inferred: {
    targeting: string;
  };
}

export function createEmptyLabelTargetFields(): LabelTargetFields {
  return {
    name: "",
    iconType: "",
    nbt: {
      kind: "",
      platform: "",
      campaignName: "",
      adsetName: "",
      adName: "",
    },
    utm: {
      source: "",
      medium: "",
      campaign: "",
      term: "",
      content: "",
    },
    referral: {
      domain: "",
    },
    adKey: {
      kind: "",
      platform: "",
      campaign: "",
      adset: "",
      ad: "",
    },
    google__campaign: {
      advertisingChannelSubType: "",
      advertisingChannelType: "",
      isDynamicSearch: "",
    },
    facebook__campaign: {
      objective: "",
    },
    inferred: {
      targeting: "",
    },
  };
}

export function getIdentificationMethod(fields: LabelTargetFields) {
  if (!fields.nbt.kind) {
    return "NBT";
  }

  if (fields.utm.source) {
    return "UTM";
  }

  if (fields.adKey.kind === "unattributed") {
    return "Unattributed";
  }

  if (fields.adKey.kind === "excluded") {
    return "Excluded";
  }

  if (fields.adKey.kind === "query") {
    return "Query Param";
  }

  return "Referral";
}

/**
 * Applies the LabelRule to the given data, representing a particular
 * traffic source.
 *
 * 1. The `matcher` is applied to the data and returns a non-falsy value
 *    iff the data matches this rule.
 * 2. If the `matcher` returns a falsy value, return an empty string.
 * 3. Otherwise, return the output. (The output is a templated string
 *    that can potentially return an empty string anyway.)
 *
 * @param labelRule the rule to apply to the given data
 * @param data the data (representing traffic source info)
 * @returns the label value. Empty string indicates no match.
 */
export function applyLabelRule(labelRule: LabelRule, data: any): string {
  const result = apply(labelRule.matcher, data);
  if (!result) {
    return "";
  }

  // Cache compute `_regexCaptures`
  let regexCaptures = labelRule._regexCaptures;
  if (!regexCaptures) {
    regexCaptures = extractRegexCaptures(labelRule.matcher);
  }

  const matches: (MatchResult | null)[] = [];
  for (const { pattern, operand: operandLogic } of regexCaptures) {
    const xrePattern = compileRegex(pattern);
    const operand: string = apply(operandLogic, data);
    const match = XRegExp.exec(operand, xrePattern);

    if (match) {
      matches.push({ __root: match[0], ...(match.groups ?? {}) });
    } else {
      matches.push(null);
    }
  }

  const outputData = { data, matches, functions: MUSTACHE_FUNCTIONS };

  return Mustache.render(labelRule.output, outputData);

  interface MatchResult {
    [key: string]: string | undefined;
    __root: string;
  }
}

function makeMustacheStringFunction(func: (v: string) => string) {
  return function () {
    return function (text: string, render: any) {
      return func(render(text));
    };
  };
}

const MUSTACHE_FUNCTIONS = {
  lower: makeMustacheStringFunction((v) => v.toLowerCase()),
  upper: makeMustacheStringFunction((v) => v.toUpperCase()),
  custom_spend_api: makeMustacheStringFunction((v) =>
    v.slice("custom-spend-".length),
  ),
};

export function applyLabelRules(labelRules: LabelRule[], data: any) {
  labelRules = _.sortBy([...labelRules], "priority", "createdAt");
  for (const labelRule of labelRules) {
    if (!labelRule.active) {
      continue;
    }
    const result = applyLabelRule(labelRule, data);
    if (result) {
      return result;
    }
  }

  return "";
}

interface MatchedRule {
  name: string;
  priority: number;
  value: string;
}

export interface LabelRuleMatchResult {
  outputValue: string;
  priorityOrderedMatchedRules: MatchedRule[];
}

export interface LabelRuleSimulationResult {
  visitsLast30d: number;
  name: string;
  identificationMethod: string;
  matchResult: LabelRuleMatchResult;
  metadata: any;
  level: string;
}
export interface LabelRuleSimulationResultV2 {
  visitsLast30d: number;
  name: string;
  identificationMethod: string;
  matchResult: LabelRuleMatchResult;
  metadata: any;
  status: string;
  labelValue: string;
  level: string;
}

export function simulateLabelRuleGroup(
  labelRules: LabelRule[],
  data: any,
): LabelRuleMatchResult {
  let outputValue = "";
  const priorityOrderedMatchedRules: MatchedRule[] = [];

  labelRules = _.sortBy([...labelRules], "priority", "createdAt");
  for (const labelRule of labelRules) {
    if (!labelRule.active) {
      continue;
    }
    const result = applyLabelRule(labelRule, data);
    if (result) {
      priorityOrderedMatchedRules.push({
        name: labelRule.name,
        priority: labelRule.priority,
        value: result,
      });
      if (!outputValue) {
        outputValue = result;
      }
    }
  }

  return { outputValue, priorityOrderedMatchedRules };
}

export interface RegexCapture {
  pattern: string;
  operand: RulesLogic;
}

export function extractRegexCaptures(matcher: RulesLogic): RegexCapture[] {
  const rv: RegexCapture[] = [];
  traverse(matcher);
  return rv;

  function traverse(current: RulesLogic) {
    // Case: primitive
    if (typeof current !== "object") {
      return;
    }

    // Case: array (each element is eval'ed)
    if (Array.isArray(current)) {
      for (const element of current) {
        traverse(element);
      }
      return;
    }

    // Case: regex operator call
    if ("~" in current) {
      const [operand, pattern] = current["~"];
      const item: RegexCapture = {
        pattern,
        operand,
      };

      rv.push(item);
      return;
    }

    // Case: (non-regex) operator call
    let args: any = Object.values(current)[0];
    if (!Array.isArray(args)) {
      args = [args];
    }

    for (const arg of args) {
      traverse(arg);
    }
  }
}

/**
 * Returns empty string if validations pass.
 * Returns a human readable error message if validation fails.
 *
 * @param output output template to check
 */
export function validateOutputTemplate(
  output: string,
  validTemplateParams: string[],
): string {
  let parsed: ReturnType<typeof Mustache.parse>;
  try {
    parsed = Mustache.parse(output);
  } catch (e) {
    return (e as any).message;
  }

  function validateInner(span: (typeof parsed)[0]): string {
    if (span[0] === "text") {
      return "";
    }
    if (span[0] === "name" || span[0] === "&") {
      if (validTemplateParams.indexOf(span[1]) < 0) {
        return `Invalid template parameter: ${span[1]}`;
      }
      return "";
    }
    if (span[0] === "#") {
      if (span[1] !== "functions.lower" && span[1] !== "functions.upper") {
        return `Invalid function: ${span[1]}`;
      }
      if (!span[4] || !Array.isArray(span[4])) {
        return `Invalid section inner: ${span[4]}`;
      }

      for (const subspan of span[4] as any) {
        const res = validateInner(subspan);
        if (res) {
          return res;
        }
      }

      return "";
    }
    return `Invalid template span type: ${span[0]}`;
  }

  for (const span of parsed) {
    const res = validateInner(span);
    if (res) {
      return res;
    }
  }

  return "";
}

export function compileRegex(pattern: string): RegExp {
  try {
    return XRegExp.cache(pattern, "");
  } catch (e) {
    // This pattern matches nothing
    return XRegExp.cache("(?!)", "");
  }
}

// RegEx match
add_operation("~", (data: string, pattern: string): boolean => {
  const xre = compileRegex(pattern);
  return XRegExp.test(data, xre);
});

add_operation("startsWith", (data: string, pattern: string): boolean => {
  return (data ?? "").startsWith(pattern);
});

add_operation("endsWith", (data: string, pattern: string): boolean => {
  return (data ?? "").endsWith(pattern);
});

add_operation("contains", (data: string, pattern: string): boolean => {
  return (data ?? "").includes(pattern);
});

add_operation("!contains", (data: string, pattern: string): boolean => {
  return !(data ?? "").includes(pattern);
});
